import { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, CalendarIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/lib/I18nContext";
import { SUPPORTED_LANGUAGES_ORDERED, LANGUAGE_LABELS } from "@/lib/i18n";

const inputCx =
  "w-full h-12 bg-[#121212] border border-white/10 rounded-md px-4 text-white placeholder:text-white/30 font-body text-sm focus:outline-none focus:ring-1 focus:ring-[#E5B83A] focus:border-[#E5B83A] transition-colors";

const labelCx = "block text-xs font-medium text-white/80 mb-2 tracking-wide uppercase font-body";

const primaryBtnCx =
  "btn-gold-shimmer w-full h-12 mt-2 rounded-md font-semibold text-sm tracking-wide " +
  "bg-gradient-to-r from-[#E5B83A] to-[#C9971C] text-black " +
  "hover:opacity-95 active:scale-[0.99] transition-all " +
  "disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2";

const tabTriggerCx =
  "flex-1 pb-3 pt-1 text-sm font-medium tracking-wide bg-transparent rounded-none " +
  "text-[#A1A1AA] data-[state=active]:text-white data-[state=active]:border-b-2 " +
  "data-[state=active]:border-[#E5B83A] transition-all";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Phase 20 — leap-year-safe age computation (mirrors backend `compute_age`).
function computeAge(isoDob) {
  if (!isoDob) return null;
  const parts = isoDob.split("-").map((n) => parseInt(n, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  const today = new Date();
  const ty = today.getFullYear();
  const tm = today.getMonth() + 1;
  const td = today.getDate();
  let age = ty - y;
  if (tm < m || (tm === m && td < d)) age -= 1;
  return age;
}

function formatIsoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Phase 20 — legal-checkbox label with the two known substrings converted
// to clickable <Link>s that open the existing Phase 13 pages. The exact
// substring pairs are locked per translation; `dangerouslySetInnerHTML`
// is intentionally avoided.
const LEGAL_LINK_PAIRS = {
  en: { terms: "Terms of Service", privacy: "Privacy Policy" },
  es: { terms: "Términos de Servicio", privacy: "Política de Privacidad" },
  it: { terms: "Termini di Servizio", privacy: "Informativa sulla Privacy" },
  asl: { terms: "Terms of Service", privacy: "Privacy Policy" }, // ASL inherits EN.
};

function renderLegalWithLinks(text, lang) {
  const pair = LEGAL_LINK_PAIRS[lang] || LEGAL_LINK_PAIRS.en;
  const linkCx = "underline text-[#FFD700] hover:text-[#F0C95C] transition-colors";
  // Build a list of {kind, value} tokens.
  const tokens = [];
  let remaining = text;
  const first = (needle, kind, to, testid) => {
    const idx = remaining.indexOf(needle);
    if (idx === -1) return;
    if (idx > 0) tokens.push({ kind: "text", value: remaining.slice(0, idx) });
    tokens.push({ kind, value: needle, to, testid });
    remaining = remaining.slice(idx + needle.length);
  };
  first(pair.terms, "link", "/terms", "auth-legal-link-terms");
  first(pair.privacy, "link", "/privacy", "auth-legal-link-privacy");
  if (remaining) tokens.push({ kind: "text", value: remaining });

  return tokens.map((tok, i) =>
    tok.kind === "text" ? (
      <span key={i}>{tok.value}</span>
    ) : (
      <Link
        key={i}
        to={tok.to}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={linkCx}
        data-testid={tok.testid}
      >
        {tok.value}
      </Link>
    )
  );
}

export default function AuthCard({ defaultTab = "login" }) {
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  const { t, language, setLanguage } = useI18n();

  const [tab, setTab] = useState(defaultTab);

  // login state
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // signup state
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [signupConfirm, setSignupConfirm] = useState("");
  const [signupConsent, setSignupConsent] = useState(false);
  const [signupAdultAck, setSignupAdultAck] = useState(false);
  const [signupDob, setSignupDob] = useState(""); // ISO YYYY-MM-DD
  const [dobPopoverOpen, setDobPopoverOpen] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupLoading, setSignupLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(language);

  const onLanguageChange = (val) => {
    setSelectedLanguage(val);
    setLanguage(val);
  };

  const passwordIssue = useMemo(() => {
    if (!signupPassword) return "";
    if (signupPassword.length < 8) return "Password must be at least 8 characters.";
    if (!/\d/.test(signupPassword)) return "Password must include a number.";
    return "";
  }, [signupPassword]);

  const confirmIssue = useMemo(() => {
    if (!signupConfirm) return "";
    if (signupConfirm !== signupPassword) return "Passwords don't match.";
    return "";
  }, [signupConfirm, signupPassword]);

  // Phase 20 — real-time age gate.
  const age = useMemo(() => computeAge(signupDob), [signupDob]);
  const isUnderage = signupDob !== "" && age !== null && age < 18;

  const signupValid =
    emailRe.test(signupEmail) &&
    !passwordIssue &&
    signupPassword.length > 0 &&
    signupConfirm === signupPassword &&
    signupConsent &&
    signupAdultAck &&
    signupDob !== "" &&
    !isUnderage;

  const loginValid = emailRe.test(loginEmail) && loginPassword.length > 0;

  async function handleLogin(e) {
    e.preventDefault();
    if (!loginValid) return;
    setLoginError("");
    setLoginLoading(true);
    try {
      const u = await login({ email: loginEmail, password: loginPassword });
      toast.success(`Welcome back, ${u.email}`);
      navigate("/app");
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleSignup(e) {
    e.preventDefault();
    if (!signupValid) return;
    setSignupError("");
    setSignupLoading(true);
    try {
      const u = await signup({
        email: signupEmail,
        password: signupPassword,
        marketing_consent: signupConsent,
        preferred_language: selectedLanguage,
        date_of_birth: signupDob,
        adult_acknowledged: signupAdultAck,
      });
      toast.success(`Account created — welcome, ${u.email}`);
      navigate("/app");
    } catch (err) {
      // Phase 20 — the backend returns 403 with `error_code: "underage"` OR
      // 400 with `error_code: "adult_ack_required"`. Both carry a translated
      // `message` field. Prefer that; fall back to the generic detail.
      const data = err?.response?.data?.detail;
      let msg = "";
      if (data && typeof data === "object") {
        msg =
          data.message ||
          data.detail ||
          formatApiErrorDetail(data);
      } else {
        msg = formatApiErrorDetail(data) || err.message;
      }
      setSignupError(msg);
    } finally {
      setSignupLoading(false);
    }
  }

  const dobFieldStyle = isUnderage
    ? {
        border: "2px solid #FF0000",
        boxShadow: "0 0 0 4px rgba(255,0,0,0.25)",
      }
    : {};

  const dobLabel = signupDob || t("auth.dob_placeholder", "Select your date of birth");

  return (
    <div
      data-testid="auth-card"
      className="w-full max-w-[460px] bg-[#0A0A0A] border border-white/10 rounded-xl p-6 sm:p-8 shadow-[0_24px_64px_-24px_rgba(0,0,0,0.9)] backdrop-blur-sm"
    >
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList
          data-testid="auth-tabs"
          className="flex w-full h-auto p-0 bg-transparent border-b border-white/10 rounded-none mb-7"
        >
          <TabsTrigger value="login" className={tabTriggerCx} data-testid="tab-login">
            Sign in
          </TabsTrigger>
          <TabsTrigger value="signup" className={tabTriggerCx} data-testid="tab-signup">
            Create account
          </TabsTrigger>
        </TabsList>

        {/* LOGIN — Phase 1 flow preserved, untouched */}
        <TabsContent value="login" className="mt-0">
          <form onSubmit={handleLogin} className="space-y-5" data-testid="login-form" noValidate>
            <div data-signup-field="skip">
              <label htmlFor="login-email" className={labelCx}>
                Email
              </label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                className={inputCx}
              />
            </div>
            <div>
              <label htmlFor="login-password" className={labelCx}>
                Password
              </label>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                autoComplete="current-password"
                placeholder="••••••••"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                className={inputCx}
              />
            </div>

            {loginError ? (
              <p data-testid="login-error" className="text-sm text-[#FF6B6B] font-body">
                {loginError}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={!loginValid || loginLoading}
              className={primaryBtnCx}
              data-testid="login-submit-button"
            >
              {loginLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {loginLoading ? "Signing in…" : "Sign in"}
            </button>

            <p className="text-xs text-[#A1A1AA] text-center font-body mt-2">
              New to FiguMatch?{" "}
              <button
                type="button"
                onClick={() => setTab("signup")}
                className="text-[#E5B83A] hover:text-[#F0C95C] transition-colors"
                data-testid="switch-to-signup"
              >
                Create an account
              </button>
            </p>
          </form>
        </TabsContent>

        {/* SIGNUP — Phase 20 field order:
              1. Email  2. Password  3. Confirm  4. DOB  5. Language
              6. Marketing consent  7. Legal 18+  8. Submit */}
        <TabsContent value="signup" className="mt-0">
          <form onSubmit={handleSignup} className="space-y-5" data-testid="signup-form" noValidate>
            {/* 1. Email */}
            <div data-signup-field="email">
              <label htmlFor="signup-email" className={labelCx}>
                Email
              </label>
              <input
                id="signup-email"
                data-testid="signup-email-input"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className={inputCx}
              />
            </div>

            {/* 2. Password */}
            <div data-signup-field="password">
              <label htmlFor="signup-password" className={labelCx}>
                Password
              </label>
              <input
                id="signup-password"
                data-testid="signup-password-input"
                type="password"
                autoComplete="new-password"
                placeholder="Min 8 characters, include a number"
                value={signupPassword}
                onChange={(e) => setSignupPassword(e.target.value)}
                className={inputCx}
              />
              {passwordIssue ? (
                <p
                  data-testid="signup-password-error"
                  className="mt-2 text-xs text-[#FF6B6B] font-body"
                >
                  {passwordIssue}
                </p>
              ) : null}
            </div>

            {/* 3. Confirm password */}
            <div data-signup-field="confirm">
              <label htmlFor="signup-confirm" className={labelCx}>
                Confirm password
              </label>
              <input
                id="signup-confirm"
                data-testid="signup-confirm-input"
                type="password"
                autoComplete="new-password"
                placeholder="Re-enter your password"
                value={signupConfirm}
                onChange={(e) => setSignupConfirm(e.target.value)}
                className={inputCx}
              />
              {confirmIssue ? (
                <p
                  data-testid="signup-confirm-error"
                  className="mt-2 text-xs text-[#FF6B6B] font-body"
                >
                  {confirmIssue}
                </p>
              ) : null}
            </div>

            {/* 4. Date of Birth (Phase 20) */}
            <div data-signup-field="date_of_birth">
              <label htmlFor="signup-dob" className={labelCx}>
                {t("auth.dob_label", "Date of Birth")}
              </label>
              <Popover open={dobPopoverOpen} onOpenChange={setDobPopoverOpen}>
                <PopoverTrigger asChild>
                  <button
                    id="signup-dob"
                    type="button"
                    data-testid="signup-dob-trigger"
                    className={
                      inputCx +
                      " flex items-center justify-between text-left cursor-pointer"
                    }
                    style={dobFieldStyle}
                    aria-invalid={isUnderage ? "true" : "false"}
                    aria-describedby={
                      isUnderage ? "dob-underage-error" : undefined
                    }
                  >
                    <span
                      className={
                        signupDob
                          ? "text-white font-mono"
                          : "text-white/30 font-body"
                      }
                    >
                      {dobLabel}
                    </span>
                    <CalendarIcon className="h-4 w-4 text-[#E5B83A]" />
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  data-testid="signup-dob-popover"
                  className="w-auto p-0 bg-[#000000] border border-white/10 text-white"
                >
                  <Calendar
                    mode="single"
                    initialFocus
                    captionLayout="dropdown-buttons"
                    fromYear={1900}
                    toYear={new Date().getFullYear()}
                    defaultMonth={new Date(2000, 0, 1)}
                    selected={signupDob ? new Date(`${signupDob}T00:00:00`) : undefined}
                    onSelect={(d) => {
                      if (d) {
                        setSignupDob(formatIsoDate(d));
                        setDobPopoverOpen(false);
                      }
                    }}
                    disabled={{ after: new Date() }}
                    classNames={{
                      day_selected:
                        "bg-[#FFD700] text-black hover:bg-[#F0C95C] hover:text-black focus:bg-[#FFD700] focus:text-black",
                      day: "h-8 w-8 p-0 font-normal text-white hover:bg-[#E5B83A]/20 rounded-md aria-selected:opacity-100",
                      day_today:
                        "bg-white/10 text-white ring-1 ring-[#E5B83A]/40",
                      day_outside:
                        "text-white/25 aria-selected:bg-accent/40",
                      day_disabled: "text-white/20 opacity-40",
                      caption_label: "text-sm font-medium text-white",
                      nav_button:
                        "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100 text-white",
                      head_cell:
                        "text-[#A1A1AA] rounded-md w-8 font-normal text-[0.75rem]",
                    }}
                  />
                </PopoverContent>
              </Popover>

              {isUnderage ? (
                <div
                  role="alert"
                  id="dob-underage-error"
                  data-testid="dob-underage-error"
                  className="mt-2 rounded-md border-2 border-[#FF0000] bg-[#3B0000]/40 px-4 py-3 text-sm text-[#FFAAAA] font-body"
                >
                  {t(
                    "auth.dob_error_underage",
                    "Access Denied. FiguMatch is strictly for users aged 18 and older."
                  )}
                </div>
              ) : null}
            </div>

            {/* 5. Preferred language */}
            <div data-signup-field="preferred_language">
              <label htmlFor="preferred-language" className={labelCx}>
                {t("auth.language_label")}
              </label>
              <select
                id="preferred-language"
                name="preferred_language"
                data-testid="signup-language-select"
                value={selectedLanguage}
                onChange={(e) => onLanguageChange(e.target.value)}
                className={inputCx + " appearance-none pr-10"}
                aria-describedby="preferred-language-help"
              >
                {SUPPORTED_LANGUAGES_ORDERED.map((code) => (
                  <option
                    key={code}
                    value={code}
                    className="bg-[#121212] text-white"
                  >
                    {LANGUAGE_LABELS[code]}
                  </option>
                ))}
              </select>
              <p
                id="preferred-language-help"
                data-testid="signup-language-help"
                className="text-xs text-[#A1A1AA]/70 mt-1.5 font-body"
              >
                {t("auth.language_help")}
              </p>
            </div>

            {/* 6. Marketing consent (Phase 1 — PRESERVED) */}
            <label
              data-signup-field="marketing_consent"
              className="flex items-start gap-3 pt-1 cursor-pointer select-none"
            >
              <Checkbox
                checked={signupConsent}
                onCheckedChange={(v) => setSignupConsent(v === true)}
                data-testid="signup-consent-checkbox"
                className="mt-0.5 border-white/30 bg-[#121212] data-[state=checked]:bg-[#E5B83A] data-[state=checked]:text-black data-[state=checked]:border-[#E5B83A] h-4 w-4 rounded-sm"
              />
              <span
                data-testid="signup-consent-label"
                className="text-xs text-[#A1A1AA] leading-relaxed font-body"
              >
                {t("auth.marketing_consent")}
              </span>
            </label>

            {/* 7. Legal 18+ acknowledgment (Phase 20) */}
            <label
              data-signup-field="adult_acknowledged"
              className="flex items-start gap-3 pt-1 cursor-pointer select-none"
            >
              <Checkbox
                checked={signupAdultAck}
                onCheckedChange={(v) => setSignupAdultAck(v === true)}
                data-testid="auth-legal-18-checkbox"
                className="mt-0.5 border-white/30 bg-[#121212] data-[state=checked]:bg-[#FFD700] data-[state=checked]:text-black data-[state=checked]:border-[#FFD700] h-4 w-4 rounded-sm"
              />
              <span
                data-testid="auth-legal-18-label"
                className="text-xs text-[#A1A1AA] leading-relaxed font-body"
              >
                {renderLegalWithLinks(
                  t("auth.legal_18_checkbox"),
                  language
                )}
              </span>
            </label>

            {signupError ? (
              <div
                data-testid="signup-error"
                role="alert"
                className="rounded-md border-2 border-[#FF0000] bg-[#3B0000]/40 px-4 py-3 text-sm text-[#FFAAAA] font-body"
              >
                {signupError}
              </div>
            ) : null}

            {/* 8. Submit */}
            <button
              type="submit"
              disabled={!signupValid || signupLoading}
              className={primaryBtnCx}
              data-testid="signup-submit-button"
            >
              {signupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {signupLoading ? "Creating account…" : "Create account"}
            </button>

            <p className="text-xs text-[#A1A1AA] text-center font-body mt-2">
              Already collecting?{" "}
              <button
                type="button"
                onClick={() => setTab("login")}
                className="text-[#E5B83A] hover:text-[#F0C95C] transition-colors"
                data-testid="switch-to-login"
              >
                Sign in instead
              </button>
            </p>
          </form>
        </TabsContent>
      </Tabs>
    </div>
  );
}
